import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { ModelBase } from "@models";
import { 
  ASIBaseFeature,
  Background, 
  Character,
  CharacterBackground,
  FeatFeature,
} from "@dndModels";

import API from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import DataUtilities from '@dndUtilities/data_utilities';

import SelectObjectIndex from "@dndComponents/select/SelectObjectIndex";
import CharacterFeatureSourcesOuter from "@dndComponents/character/features/CharacterFeatureSourcesOuter";

interface Props {
  char: Character;
}

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const data_util = DataUtilities.getInstance();

export default function CharacterBackgroundInput({
  char,
}: Props) {
  const [change_background, setChangeBackground] = useState(false);
  const [filter, setFilter] = useState<any>({});
  const [reloading, setReloading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [char_background, setCharBackground] = useState(char.background);

  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const recalculated = useCallback(() => {
    console.log("Char Background - Recalculated");
    setTimeout(() => {
      setReloading(true);
    }, 100);
  }, []);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
      if (processing) {
        setProcessing(false);
        setCharBackground(char.background);
        setChangeBackground(false);
      }
    }
  }, [reloading, processing, char]);

  useEffect(() => {
    char.subscription_holder.subscribe("Recalculated", mounted, recalculated);
  }, [recalculated]);

  const updateFilter = (changed: any = {}) => {
    const new_filter = {...changed};
    setFilter(new_filter);
  }

  if (!char_background || char_background.source_id === "" || change_background) {
    return (
      <Grid container spacing={0} direction="row">
        { char_background && char_background.source && change_background && 
          <Grid size={12}>
            { char_background.source.true_name } 
            <Button 
              fullWidth variant="contained" color="primary" 
              onClick={ () => {
                setChangeBackground(false);
              }}>
                Keep Background
            </Button>
          </Grid>
        }
        { processing ?
          <Grid size={12} className="header centered">Setting Background</Grid>
        :
          <Grid size={12}>
            <SelectObjectIndex
              data_type="background"
              character={char}
              quick_fields={["feat","ability_scores","tags"]}
              filter={filter}
              updateFilter={updateFilter}
              onClick={ async (background2: ModelBase) => {
                if (background2 instanceof Background) {
                  // debugger;
                  setProcessing(true);
                  char.changed();
                  char_util.removeCharFeatureBasesFor(char, char.background);
                  char.background.char_feature_bases = [];
                  await api.loadObject(background2);
                  const changed = new CharacterBackground();
                  await changed.copy_source({ char: char, feature_source: background2 });
                  char.background = changed;

                  char.background.table = await data_util.getTableAsync(background2);
                  setTimeout(() => {
                    char.subscription_holder.recalculate(); 
                  }, 100);
                }
              }}
              disableObj={(obj: ModelBase) => {
                let disabled = false;
                let reasons = "";
                if (obj instanceof Background && char.background.source_id === obj._id) {
                  disabled = true;
                  reasons = "You already have this background";
                }
                return { disabled, reasons };
              }}
              detailFormat={(obj: ModelBase) => {
                if (obj instanceof Background) {
                  // Get Feat and ASI features and display if included.
                  const feat_fb = obj.feature_bases.find(o => o.features.filter(o2 => o2.feature_type === "Feat").length > 0);
                  const asi_fb = obj.feature_bases.find(o => o.features.filter(o2 => o2.feature_type === "Ability Score Improvement").length > 0);
                  let detail_str = "";
                  if (feat_fb) {
                    const f = feat_fb.features.find(o => o.feature_type === "Feat");
                    if (f && f.floating_feature instanceof FeatFeature) {
                      const ff = f.floating_feature;
                      const feat = data_util.feats.find(o => ff.feat_ids.includes(o._id));
                      if (feat) {
                        detail_str = `Feat: ${feat.name}`;
                      }
                    }
                  }
                  if (asi_fb) {
                    const f = asi_fb.features.find(o => o.feature_type === "Ability Score Improvement");
                    if (f && f.floating_feature instanceof ASIBaseFeature) {
                      const ff = f.floating_feature;
                      if (detail_str.length > 0) {
                        detail_str += ", ";
                      }
                      detail_str += `ASI: ${ff.name}`;
                    }
                  }
                  return `${obj.get_use_name()} ${detail_str}`;
                }
                return "";
              }}
            />
          </Grid>
        }
      </Grid>
    );
  } else if (char_background.source) {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          className="header"
          size={6}>
          { char_background.source && <span>{ char_background.source.true_name }</span> }
        </Grid>
        <Grid size={6}>
          <Button 
            fullWidth variant="contained" color="primary" 
            onClick={ () => {
              setChangeBackground(true);
            }}>
              Change Background
          </Button>
        </Grid>
        <Grid size={12}>
          <CharacterFeatureSourcesOuter
            char={char}
            sources={[char_background]}
            change_time={0}
            expanded_cfs_id={char_background.true_id}
            onCFSSelect={() => { }}
          />
        </Grid>
      </Grid>
    );
  } else return null;
}
